import { useMemo } from "react";
import { roundTo2 } from "utils/formators";
import { formatReviewOfferCopy, getReviewOfferCopy } from "./reviewOfferCopy";
import { DEFAULT_REVIEW_OFFER_PROGRAM, ReviewOfferEntitlement } from "./types";

export type ReviewDiscountSummary = {
  /** true only when there's an active entitlement AND the review discount is the order's current choice (HUB-454 exclusivity — see useDiscountExclusivity) */
  isApplied: boolean;
  label: string;
  amountOff: number;
  displayTotal: number;
};

/**
 * Display-only projection for S4: computes the "Review discount (5%)" line
 * and the adjusted total. Deliberately does NOT persist anything via
 * updateCampaignExtraData (unlike the coupon flow) — the real application
 * happens server-side against a Stripe promotion code at payment time
 * (A2/HUB-450, A3/HUB-451). This hook only mirrors that math for the FE
 * preview, following the negative-line pattern in
 * useCampaignCostPreviewCalculation/index.ts:78-92.
 *
 * `isReviewDiscountChosen` (HUB-454) replaces the old raw `!hasStripeCoupon`
 * check — exclusivity is now driven by useDiscountExclusivity's
 * `discountChoice`, so a swap can deselect the review discount for this order
 * even while the entitlement itself stays active/intact server-side.
 *
 * HUB-466: `label` is resolved per the entitlement's OWN `program` and
 * interpolates the entitlement's OWN `discount_percent` — never a hardcoded
 * "5%" and never the program registry's current value, so a 10%
 * (POST_CAMPAIGN_10) entitlement renders "Review discount (10%)".
 */
export const useReviewDiscountSummary = ({
  entitlement,
  subtotal,
  total,
  isReviewDiscountChosen,
}: {
  entitlement: ReviewOfferEntitlement | null;
  subtotal: number;
  total: number;
  isReviewDiscountChosen: boolean;
}): ReviewDiscountSummary =>
  useMemo(() => {
    const isApplied = !!entitlement && entitlement.status === "active" && isReviewDiscountChosen;
    const copy = getReviewOfferCopy(entitlement?.program ?? DEFAULT_REVIEW_OFFER_PROGRAM);
    if (!isApplied || !entitlement) {
      return {
        isApplied: false,
        label: formatReviewOfferCopy(copy.summary.discountLine, entitlement?.discount_percent ?? 0),
        amountOff: 0,
        displayTotal: total,
      };
    }
    const amountOff = roundTo2((entitlement.discount_percent / 100) * subtotal);
    return {
      isApplied: true,
      label: formatReviewOfferCopy(copy.summary.discountLine, entitlement.discount_percent),
      amountOff,
      displayTotal: roundTo2(Math.max(total - amountOff, 0)),
    };
  }, [entitlement, subtotal, total, isReviewDiscountChosen]);
